import { QueryCache, QueryClient } from "@tanstack/react-query";
import { ApiError, SessionExpiredError } from "@/lib/api-client";

export const queryClient = new QueryClient({
  // A session that ran out mid-visit goes to sign-in rather than an error card.
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof SessionExpiredError) window.location.assign("/login");
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Only a server or network failure is worth repeating.
      retry: (failures, error) => {
        if (error instanceof SessionExpiredError) return false;
        if (error instanceof ApiError && error.status < 500) return false;
        return failures < 1;
      },
    },
  },
});

// ── Query key factories ──

// Filter + sort state that parameterizes a paginated list query key. Mirrors
// `DataGridQuery` from `@/hooks/use-data-grid`; kept structural to avoid a cycle.
interface ListKeyFilters {
  search?: string;
  status?: string;
  sort?: string;
  sortDir?: string;
}

// --- S3_FEATURE_START ---
export const fileKeys = {
  all: ["files"] as const,
  list: (filters: ListKeyFilters) => ["files", "list", filters] as const,
  detail: (id: string) => ["files", "detail", id] as const,
} as const;
// --- S3_FEATURE_END ---

export const userKeys = {
  all: ["users"] as const,
  list: (filters?: { search?: string }) => ["users", "list", filters] as const,
} as const;

export const authKeys = {
  options: ["auth", "options"] as const,
  quickLogin: ["auth", "quick-login"] as const,
} as const;

export const emailKeys = {
  all: ["email"] as const,
  outbox: () => ["email", "outbox"] as const,
  templates: () => ["email", "templates"] as const,
  template: (key: string) => ["email", "templates", key] as const,
} as const;

export const auditKeys = {
  all: ["audit"] as const,
  list: (filters: ListKeyFilters) => ["audit", "list", filters] as const,
} as const;

interface PhotoListFilters {
  album?: string;
  group?: string;
  from?: string;
  to?: string;
  company?: string;
  make?: string;
  model?: string;
  serial?: string;
  sortDir?: string;
}

interface PinFilters {
  album?: string;
  from?: string;
  to?: string;
}

export const projectKeys = {
  all: ["projects"] as const,
  list: (filters: ListKeyFilters, cursor: string | null) =>
    ["projects", "list", filters, cursor] as const,
  detail: (id: string) => ["projects", "detail", id] as const,
  photos: (id: string, filters: PhotoListFilters) =>
    ["projects", "detail", id, "photos", filters] as const,
  pins: (id: string, filters: PinFilters) => ["projects", "detail", id, "pins", filters] as const,
  photo: (id: string, photoId: string) => ["projects", "detail", id, "photo", photoId] as const,
} as const;

export const shareKeys = {
  all: ["shares"] as const,
  list: (projectId: string, filters: ListKeyFilters & { scope?: string }, cursor: string | null) =>
    ["shares", "list", projectId, filters, cursor] as const,
  summary: (projectId: string) => ["shares", "summary", projectId] as const,
  photo: (photoId: string) => ["shares", "photo", photoId] as const,
} as const;

export const checklistKeys = {
  all: ["checklist"] as const,
  project: (projectId: string) => ["checklist", projectId] as const,
} as const;

export const extractionKeys = {
  all: ["extractions"] as const,
  photo: (photoId: string) => ["extractions", photoId] as const,
} as const;

export const importReportKeys = {
  latest: ["import-report", "latest"] as const,
} as const;

export const zipKeys = {
  all: ["zips"] as const,
  list: ["zips", "list"] as const,
} as const;
